"use client"

import * as React from "react"
import { Select as SelectPrimitive } from "@base-ui/react/select"
import {
  assertViewStateValue,
  type FieldControlBinding,
  ViewStateMode,
  useViewStateField,
  type ViewStateControlProps,
  type ViewStateValue,
  type CollectionSource,
} from "@florisoft/framework"

import { cn } from "../../lib/utils"
import type { ControlChrome } from "../../lib/control-chrome"
import { Button } from "./button"
import { fieldControlFocusStyles, fieldControlShellStyles, updateFieldControlPointer } from "./field-control"
import { useFieldControlProps } from "./field"
import { anchoredPopupSurfaceStyles } from "./popup-styles"
import { popupScrollAreaStyles, ScrollAreaRoot, ScrollAreaViewport, ScrollBar } from "./scroll-area"
import { CheckMarkIcon } from "../../icons/check-mark"
import { optionCheckStyles, optionCheckboxStyles } from "./option-styles"
import { ChevronDownIcon } from "../../icons/chevron-down"
import { ChevronUpIcon } from "../../icons/chevron-up"
import { PopupActionButton } from "./popup-action-button"
import { CrossIcon } from "../../icons/cross"
import { useUiTranslation } from "../../localization/use-ui-translation"
import { useCollectionOptions } from "./use-collection-options"
import { usePortalContainer } from "./portal-container"

/** The value a form `field` binds to a `Select`: an array of values when `multiple`, otherwise one value or `null`. */
type SelectFieldValue<Value, Multiple extends boolean | undefined> = Multiple extends true ? Value[] : Value | null

type SelectProps<Value, Multiple extends boolean | undefined = false> = SelectPrimitive.Root.Props<Value, Multiple> &
  ViewStateControlProps & {
    /**
     * Surrounding decoration of the control and, unless a part sets its own, of `SelectTrigger`. Omission inherits the
     * container default, otherwise bordered.
     */
    chrome?: ControlChrome
    /**
     * Binds the value, name and validation state to a form field. Cannot be combined with `value` or `defaultValue`.
     */
    field?: FieldControlBinding<SelectFieldValue<Value, Multiple>>
    /** Renders the content of each option of a `source` without children; defaults to the label from `getLabel`. */
    renderItem?: (item: Value) => React.ReactNode
    /** Largest number of `source` items shown as options. Defaults to 200. */
    maxItems?: number
    /** Clicks on this element (a host trigger) do not dismiss the popup; also positions the assembled popup. */
    anchor?: React.RefObject<HTMLElement | null>
    /** Assembled control only: these apply when a source is given without children. */
    className?: string
    /** Assembled control only. */
    triggerProps?: React.ComponentProps<typeof SelectTrigger>
    /** Assembled control only. */
    showTrigger?: boolean
    /** Assembled control only. */
    showClear?: boolean
  } & (
    | {
        /**
         * Collection that supplies the options. Without children, Select assembles trigger, popup and items from it.
         */
        source?: undefined
        /** Text of an item, used for the shown value and for the default option content. Required with `source`. */
        getLabel?: (item: Value) => string
      }
    | {
        /**
         * Collection that supplies the options. Without children, Select assembles trigger, popup and items from it.
         */
        source: CollectionSource<Value>
        /** Text of an item, used for the shown value and for the default option content. Required with `source`. */
        getLabel: (item: Value) => string
      }
  )

type SelectValueChangeHandler<Value, Multiple extends boolean | undefined = false> = NonNullable<
  SelectProps<Value, Multiple>["onValueChange"]
>

type SelectContextValue = {
  chrome?: ControlChrome
  anchorRef: React.RefObject<HTMLDivElement | null>
  value: unknown
  multiple: boolean
  disabled: boolean
  readOnly: boolean
  id?: string
  invalid: boolean
  blur: () => void
  clear: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Source loading state for parts composed inside a source-backed Select. */
  collection: ReturnType<typeof useCollectionOptions> | null
}

const SelectContext = React.createContext<SelectContextValue | null>(null)

/**
 * Dropdown for choosing one value, or several with `multiple`, from a list of options. Compose `SelectTrigger` with
 * `SelectContent` and `SelectItem`, or pass a `source` and `getLabel` for the assembled control. Use Combobox
 * instead when the user must type to search the options.
 */
function Select<Value, Multiple extends boolean | undefined = false>({
  children,
  chrome,
  field,
  id,
  name,
  value,
  defaultValue,
  onValueChange,
  onOpenChange,
  multiple,
  disabled = false,
  readOnly = false,
  viewState = ViewStateMode.None,
  viewStateId,
  source,
  getLabel,
  renderItem,
  maxItems,
  className,
  triggerProps,
  anchor,
  showTrigger = true,
  showClear = false,
  open,
  defaultOpen = false,
  ...props
}: SelectProps<Value, Multiple>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const currentOpen = open ?? uncontrolledOpen
  const options = useCollectionOptions<Value, never>({ source, maxItems, open: currentOpen })
  const isMultiple = multiple === true
  const isFieldBound = field !== undefined
  const isControlled = isFieldBound || value !== undefined
  const isViewStateManaged = viewState !== ViewStateMode.None
  const initialViewStateValue = (defaultValue ?? (isMultiple ? [] : null)) as ViewStateValue
  const managedViewState = useViewStateField({
    defaultValue: initialViewStateValue,
    viewState,
    viewStateId,
  })
  const anchorRef = React.useRef<HTMLDivElement | null>(null)
  const [uncontrolledValue, setUncontrolledValue] = React.useState<unknown>(
    () => defaultValue ?? (isMultiple ? [] : null)
  )
  if (isViewStateManaged && isControlled) {
    throw new Error("Select viewState cannot be combined with controlled value.")
  }

  if (isFieldBound && (value !== undefined || defaultValue !== undefined)) {
    throw new Error("Select field cannot be combined with value or defaultValue.")
  }

  const currentValue = (
    isViewStateManaged ? managedViewState.value : isFieldBound ? field.value : isControlled ? value : uncontrolledValue
  ) as SelectProps<Value, Multiple>["value"]

  const handleValueChange = React.useCallback<SelectValueChangeHandler<Value, Multiple>>(
    (nextValue, eventDetails) => {
      if (isFieldBound) {
        field.setValue(nextValue as SelectFieldValue<Value, Multiple>)
      } else if (isViewStateManaged) {
        assertViewStateValue(nextValue)
        managedViewState.setValue(nextValue)
      } else if (!isControlled) {
        setUncontrolledValue(nextValue)
      }
      onValueChange?.(nextValue, eventDetails)
    },
    [field, isControlled, isFieldBound, isViewStateManaged, managedViewState, onValueChange]
  )

  const clear = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const nextValue = (isMultiple ? [] : null) as Parameters<SelectValueChangeHandler<Value, Multiple>>[0]

      handleValueChange(nextValue, {
        reason: "none",
        event: event.nativeEvent,
        cancel: () => {},
        allowPropagation: () => {},
        isCanceled: false,
        isPropagationAllowed: true,
        trigger: event.currentTarget,
      })
    },
    [handleValueChange, isMultiple]
  )

  return (
    <SelectPrimitive.Root
      {...props}
      items={source && getLabel ? options.items.map((item) => ({ value: item, label: getLabel(item) })) : props.items}
      itemToStringLabel={getLabel ?? props.itemToStringLabel}
      isItemEqualToValue={
        source ? (item, selected) => source.getKey(item) === source.getKey(selected) : props.isItemEqualToValue
      }
      open={currentOpen}
      id={id}
      multiple={multiple}
      disabled={disabled}
      name={name ?? field?.name}
      readOnly={readOnly}
      value={currentValue}
      onValueChange={handleValueChange}
      onOpenChange={(nextOpen, details) => {
        if (!nextOpen && details.event.target instanceof Node && anchor?.current?.contains(details.event.target)) {
          details.cancel()
          return
        }
        onOpenChange?.(nextOpen, details)
        if (details.isCanceled) return
        setUncontrolledOpen(nextOpen)
        if (!nextOpen && field) {
          queueMicrotask(() => {
            if (!anchorRef.current?.contains(document.activeElement)) {
              field.blur()
            }
          })
        }
      }}
    >
      <SelectContext.Provider
        value={{
          chrome,
          anchorRef,
          value: currentValue,
          multiple: isMultiple,
          disabled,
          readOnly,
          id,
          invalid: Boolean(field?.errors.length),
          blur: () => field?.blur(),
          clear,
          collection: source ? options : null,
        }}
      >
        {children ??
          (source && getLabel ? (
            <>
              <SelectTrigger {...triggerProps} className={className} showTrigger={showTrigger} showClear={showClear}>
                <SelectValue>
                  {Array.isArray(currentValue)
                    ? currentValue.map((item: Value) => getLabel(item)).join(", ")
                    : currentValue == null
                      ? null
                      : getLabel(currentValue as Value)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent anchor={anchor} sideOffset={anchor ? 2 : 8}>
                {options.items.map((item) => (
                  <SelectItem key={source.getKey(item)} value={item}>
                    {renderItem?.(item) ?? getLabel(item)}
                  </SelectItem>
                ))}
              </SelectContent>
            </>
          ) : null)}
      </SelectContext.Provider>
    </SelectPrimitive.Root>
  )
}

/** Groups related `SelectItem`s inside `SelectContent`, optionally headed by a `SelectLabel`. */
function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-part="select-group" className={cn("scroll-my-1 p-0", className)} {...props} />
}

/** Shows the selected value, or the placeholder when nothing is selected. Goes inside a `SelectTrigger`. */
function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-part="select-value"
      className={cn("min-w-0 flex-1 truncate text-left text-ui-12 font-normal", className)}
      {...props}
    />
  )
}

/**
 * The field-like control that shows the selected value and opens the popup. Goes inside a `Select`, before
 * `SelectContent`.
 */
function SelectTrigger({
  className,
  chrome,
  children,
  onBlur,
  showClear = false,
  showTrigger = true,
  ...props
}: SelectPrimitive.Trigger.Props & {
  /** Surrounding decoration of the control. Overrides the `chrome` of the `Select`; omission inherits it. */
  chrome?: ControlChrome
  /** Shows a button that clears the selection while a value is selected. Defaults to false. */
  showClear?: boolean
  /** Shows the open/close action at the end of the control. Hidden while the clear button shows. Defaults to true. */
  showTrigger?: boolean
}) {
  const selectContext = React.useContext(SelectContext)
  const { t } = useUiTranslation()

  if (!selectContext) {
    throw new Error("SelectTrigger must be used within Select")
  }

  const hasValue = selectContext.multiple
    ? Array.isArray(selectContext.value) && selectContext.value.length > 0
    : selectContext.value !== null && selectContext.value !== undefined
  const clearVisible = showClear && hasValue
  const controlProps = useFieldControlProps(
    {
      ...props,
      id: props.id ?? selectContext.id,
    },
    { invalid: selectContext.invalid }
  )

  return (
    <div
      data-part="select-control"
      data-field-control-highlight=""
      data-control-chrome={chrome ?? selectContext.chrome}
      ref={selectContext.anchorRef}
      className={cn(fieldControlShellStyles(), fieldControlFocusStyles, className)}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        const popupOpen = event.currentTarget.querySelector('[data-part="select-trigger"][data-popup-open]') != null
        if (!popupOpen) selectContext.blur()
      }}
      onPointerMove={updateFieldControlPointer}
    >
      <SelectPrimitive.Trigger
        render={<Button variant="ghost" size="default" shape="rounded" />}
        data-part="select-trigger"
        data-field-control-input="true"
        className={cn(
          "group/select-trigger h-full w-full !min-w-0 justify-between gap-[var(--fl-layout-input-fields-gap)] whitespace-nowrap select-none pl-[var(--fl-layout-input-fields-padding-horizontal)] !pr-0 py-[var(--fl-layout-input-fields-padding-vertical)] text-ui-12 font-normal hover:!bg-transparent focus-visible:!bg-transparent data-popup-open:!bg-transparent data-pressed:!bg-transparent data-placeholder:text-[var(--fl-color-content-default-secondary)] disabled:cursor-not-allowed disabled:text-[var(--fl-color-content-default-disabled)] disabled:[&_svg]:text-[var(--fl-color-content-default-disabled)] aria-invalid:text-[var(--fl-color-content-error-primary)] group-data-[status=success]/field:text-[var(--fl-color-content-default-default)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          clearVisible && "!pr-10"
        )}
        {...controlProps}
        onBlur={onBlur}
      >
        {children}
        {!clearVisible && showTrigger && (
          <SelectPrimitive.Icon
            render={(iconProps, state) => (
              <PopupActionButton
                {...iconProps}
                open={state.open}
                render={<span />}
                nativeButton={false}
                role="presentation"
                tabIndex={-1}
                aria-hidden="true"
                data-part="select-trigger-action"
                className="pointer-events-auto"
              />
            )}
          />
        )}
      </SelectPrimitive.Trigger>
      {clearVisible && (
        <Button
          type="button"
          data-part="select-clear"
          shape="rounded"
          variant="ghost"
          size="icon"
          aria-label={t(($) => $.common.clearSelection)}
          disabled={selectContext.disabled || selectContext.readOnly}
          onClick={selectContext.clear}
          className="absolute inset-y-0 right-0 z-10 shrink-0"
        >
          <CrossIcon className="pointer-events-none text-[var(--fl-color-content-default-secondary)]" />
        </Button>
      )}
    </div>
  )
}

/**
 * The popup that lists the options, anchored to the trigger. Goes inside a `Select`; holds `SelectItem`s,
 * `SelectGroup`s and `SelectSeparator`s.
 */
function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 8,
  align = "end",
  alignOffset = 0,
  alignItemWithTrigger = false,
  anchor,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger" | "anchor"
  >) {
  const selectContext = React.useContext(SelectContext)
  const collection = selectContext?.collection
  const { t } = useUiTranslation()

  return (
    <SelectPrimitive.Portal container={usePortalContainer()}>
      <SelectPrimitive.Positioner
        anchor={anchor ?? selectContext?.anchorRef}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-part="select-popup"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            anchoredPopupSurfaceStyles,
            "group/select-popup flex max-h-(--available-height) w-max min-w-(--anchor-width) max-w-(--available-width) flex-col",
            className
          )}
          {...props}
        >
          <SelectScrollUpButton />
          {/* The listbox is the scroll area's viewport: Base UI scrolls and measures its list element, for the
              scroll arrows and for aligning the selected item with the trigger. Items take focus, so the list
              itself is not a tab stop. */}
          <ScrollAreaRoot data-part="select-scroll-area" className={popupScrollAreaStyles}>
            <SelectPrimitive.List
              tabIndex={-1}
              className="max-h-(--available-height) overscroll-contain p-0"
              render={(listProps) => <ScrollAreaViewport {...listProps} />}
            >
              {children}
            </SelectPrimitive.List>
            <ScrollBar />
          </ScrollAreaRoot>
          {/* Outside the listbox: only options belong there, and Tab reaches these actions within the popup. */}
          {collection && (
            <div data-part="select-collection-status" className="shrink-0">
              {collection.feedback}
              {collection.empty && (
                <div role="status" className="px-4 py-2 text-ui-12">
                  {t(($) => $.common.noOptions)}
                </div>
              )}
              {collection.paging}
            </div>
          )}
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

/** Heading of a `SelectGroup`. */
function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-part="select-label"
      className={cn("px-4 py-2 text-ui-12 font-semibold text-[var(--fl-color-content-default-default)]", className)}
      {...props}
    />
  )
}

/** One option of the list. Its `value` is the value the `Select` takes when the option is chosen. */
function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  const multiple = React.useContext(SelectContext)?.multiple ?? false
  const indicator = (
    <span data-part="select-item-indicator" className={multiple ? optionCheckboxStyles : optionCheckStyles}>
      <SelectPrimitive.ItemIndicator className="grid place-content-center [&>svg]:size-4">
        <CheckMarkIcon className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </span>
  )
  return (
    <SelectPrimitive.Item
      data-part="select-item"
      className={cn(
        "group/select-item relative flex h-10 w-full cursor-default items-center gap-[var(--fl-layout-input-fields-gap)] rounded-none px-4 py-0 text-ui-12 font-normal text-[var(--fl-color-content-default-default)] outline-hidden select-none data-selected:bg-[var(--fl-color-background-outlined-selected)] data-selected:font-semibold data-highlighted:bg-[var(--fl-color-background-outlined-hover)] data-selected:data-highlighted:bg-[var(--fl-color-background-outlined-selected)] data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg]:text-[var(--fl-color-content-default-default)] data-disabled:[&_svg]:text-[var(--fl-color-content-default-disabled)]",
        className
      )}
      {...props}
    >
      {multiple && indicator}
      <SelectPrimitive.ItemText className="min-w-0 flex-1 truncate whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      {!multiple && indicator}
    </SelectPrimitive.Item>
  )
}

/** Thin line between options or groups inside `SelectContent`. */
function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-part="select-separator"
      className={cn("pointer-events-none my-1 h-px bg-[var(--fl-color-border-outlined-default)]", className)}
      {...props}
    />
  )
}

/**
 * Arrow at the top of the popup that scrolls the list up while pointed at; shown by `SelectContent` when the list
 * overflows.
 */
function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-part="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-[var(--fl-color-background-secondary-default)] py-1 text-[var(--fl-color-content-default-secondary)] [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  )
}

/**
 * Arrow at the bottom of the popup that scrolls the list down while pointed at; shown by `SelectContent` when the list
 * overflows.
 */
function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-part="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-[var(--fl-color-background-secondary-default)] py-1 text-[var(--fl-color-content-default-secondary)] [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
